import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

type VoiceRecordingContextValue = {
    currentSessionId: string | null;
    startSession: (sessionId: string) => boolean;
    endSession: (sessionId: string) => void;
};

const VoiceRecordingContext = createContext<VoiceRecordingContextValue | null>(null);

type VoiceRecordingProviderProps = {
    children: ReactNode;
};

export const VoiceRecordingProvider = ({ children }: VoiceRecordingProviderProps) => {
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

    const value = useMemo(() => {
        const startSession = (sessionId: string): boolean => {
            // Only allow starting if no session is active or this is the current session
            if (currentSessionId === null || currentSessionId === sessionId) {
                setCurrentSessionId(sessionId);
                return true;
            }
            return false;
        };

        const endSession = (sessionId: string): void => {
            // Only end session if this session is the current one.
            setCurrentSessionId((current) => (current === sessionId ? null : current));
        };

        return {
            currentSessionId,
            startSession,
            endSession,
        };
    }, [currentSessionId]);

    return <VoiceRecordingContext.Provider value={value}>{children}</VoiceRecordingContext.Provider>;
};

export const useVoiceRecording = (): VoiceRecordingContextValue => {
    const context = useContext(VoiceRecordingContext);

    if (!context) {
        throw new Error('useVoiceRecording must be used within a VoiceRecordingProvider');
    }

    return context;
};
